import { useMemo, useState, type FormEvent } from 'react';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconCheck, IconChevronDown, IconClose, IconEdit, IconTrash } from '../components/icons';
import { Modal } from '../components/Modal';
import Pagination from '../components/Pagination';
import SearchableSelect from '../components/SearchableSelect';
import { useAuth } from '../auth/useAuth';
import { labelFor, listOptions, useConfig } from '../context/ConfigContext';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import usePaginatedList from '../hooks/usePaginatedList';

interface Filters {
  search: string;
  status: string;
  dateFrom: string;
  dateTo: string;
}

const emptyFilters: Filters = { search: '', status: '', dateFrom: '', dateTo: '' };

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

interface KpiScore {
  id: number;
  kpi_name: string;
  weight: number;
  score: number | null;
  kra_name: string | null;
}

interface CompetencyScore {
  id: number;
  competency_name: string;
  weight: number;
  score: number | null;
}

interface ReviewRow {
  id: number;
  user_id: number;
  user_name: string;
  employee_code: string | null;
  period: string;
  final_score: number | null;
  kpi_scores: KpiScore[];
  competency_score: number | null;
  competency_scores: CompetencyScore[];
  comments: string | null;
  status: 'draft' | 'finalized';
  finalized_at: string | null;
  finalized_by_name: string | null;
  decision_comment: string | null;
  is_draft: boolean;
  approval_levels_required: number;
  current_approval_level: number;
  pending_with_name: string | null;
  approvals: ApprovalHistoryEntry[];
}

interface ApprovalHistoryEntry {
  level: number;
  approver_name: string | null;
  decision: string;
  comment: string | null;
  decided_at: string;
}

interface UserOption {
  id: number;
  full_name: string;
  [key: string]: unknown;
}

const STATUS_BADGE: Record<ReviewRow['status'], string> = {
  draft: 'badge-scheduled',
  finalized: 'badge-active',
};

const emptyForm = { user_id: '' as string | number, period: '', comments: '' };

export function Performance() {
  const { can } = useAuth();
  const { lists } = useConfig();
  const confirmAction = useConfirm();
  const { showError } = useToast();

  const [filters, setFilters] = useState<Filters>(emptyFilters);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState<ReviewRow | null>(null);
  const [editForm, setEditForm] = useState({ period: '', comments: '' });
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [scoreDrafts, setScoreDrafts] = useState<Record<number, string>>({});
  const [competencyScoreDrafts, setCompetencyScoreDrafts] = useState<Record<number, string>>({});

  const canCreate = can('hr.create');
  const canUpdate = can('hr.update');
  const canApprove = can('hr.approve');
  const canArchive = can('hr.archive');

  const query = useMemo(
    () => ({
      search: filters.search || undefined,
      status: filters.status || undefined,
      date_from: filters.dateFrom || undefined,
      date_to: filters.dateTo || undefined,
    }),
    [filters]
  );

  const { data, loading, error, setPage, reload } = usePaginatedList<ReviewRow>('/performance-reviews', query);

  const toggleExpanded = (row: ReviewRow) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(row.id)) next.delete(row.id);
      else {
        next.add(row.id);
        setScoreDrafts((drafts) => {
          const updated = { ...drafts };
          row.kpi_scores.forEach((s) => {
            updated[s.id] = s.score !== null ? String(s.score) : '';
          });
          return updated;
        });
        setCompetencyScoreDrafts((drafts) => {
          const updated = { ...drafts };
          row.competency_scores.forEach((s) => {
            updated[s.id] = s.score !== null ? String(s.score) : '';
          });
          return updated;
        });
      }
      return next;
    });
  };

  const openAdd = () => {
    setForm(emptyForm);
    setErrors({});
    setAdding(true);
  };

  const submitAdd = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/performance-reviews', form);
      setAdding(false);
      reload();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const openEdit = (row: ReviewRow) => {
    setEditing(row);
    setEditForm({ period: row.period, comments: row.comments ?? '' });
    setErrors({});
  };

  const submitEdit = async (e: FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    try {
      await api.put(`/performance-reviews/${editing.id}`, editForm);
      setEditing(null);
      reload();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const saveScore = async (score: KpiScore) => {
    const value = scoreDrafts[score.id];
    try {
      await api.put(`/performance-review-kpi-scores/${score.id}`, { score: value === '' ? null : Number(value) });
      reload();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Save failed.');
    }
  };

  const saveCompetencyScore = async (score: CompetencyScore) => {
    const value = competencyScoreDrafts[score.id];
    try {
      await api.put(`/performance-review-competency-scores/${score.id}`, { score: value === '' ? null : Number(value) });
      reload();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Save failed.');
    }
  };

  const decide = async (row: ReviewRow, decision: 'approved' | 'rejected') => {
    const verb = decision === 'approved' ? 'Approve' : 'Reject';
    const confirmed = await confirmAction(
      `${verb} ${row.user_name}'s "${labelFor(lists, 'performance_review_period', row.period)}" review?`,
      { confirmLabel: verb }
    );
    if (!confirmed) return;
    try {
      await api.post(`/performance-reviews/${row.id}/decide`, { decision });
      reload();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : `${verb} failed.`);
    }
  };

  const remove = async (row: ReviewRow) => {
    if (!(await confirmAction(`Delete this review for ${row.user_name}?`))) return;
    try {
      await api.delete(`/performance-reviews/${row.id}`);
      reload();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  return (
    <>
      <header className="page-header">
        <h1>Performance</h1>
        {canCreate && (
          <button type="button" className="btn btn-primary" onClick={openAdd}>
            + Add
          </button>
        )}
      </header>

      <div className="toolbar">
        <input
          type="search"
          placeholder="Search by name…"
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
        />
        <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="finalized">Finalized</option>
        </select>
        <label className="checkbox-inline">
          Date from
          <input type="date" value={filters.dateFrom} onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })} />
        </label>
        <label className="checkbox-inline">
          Date to
          <input type="date" value={filters.dateTo} onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })} />
        </label>
      </div>
      {(filters.dateFrom || filters.dateTo) && (
        <p className="muted">
          Date filters use the Finalized Date — draft reviews (no Finalized Date yet) are excluded while a date filter is set.
        </p>
      )}

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th></th>
                <th>Employee Code</th>
                <th>Name</th>
                <th>Period</th>
                <th>Final Score</th>
                <th>Competency Score</th>
                <th>Status</th>
                <th>Finalized Date</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} className="muted center">
                    Loading…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={9} className="error center">
                    {error}
                  </td>
                </tr>
              ) : data && data.data.length > 0 ? (
                data.data.flatMap((row) => {
                  const isExpanded = expanded.has(row.id);
                  const rows = [
                    <tr key={row.id}>
                      <td>
                        <button
                          type="button"
                          className="btn icon-btn btn-ghost"
                          aria-label={isExpanded ? 'Collapse' : 'Expand'}
                          onClick={() => toggleExpanded(row)}
                        >
                          <IconChevronDown
                            className="icon-btn-svg"
                            style={{ transform: isExpanded ? 'rotate(180deg)' : undefined, transition: 'transform 0.15s' }}
                          />
                        </button>
                      </td>
                      <td className="muted">{row.employee_code ?? '—'}</td>
                      <td>
                        <strong>{row.user_name}</strong>
                      </td>
                      <td>{labelFor(lists, 'performance_review_period', row.period)}</td>
                      <td>{row.final_score !== null ? `${row.final_score.toFixed(1)} / 5` : '—'}</td>
                      <td>{row.competency_score !== null ? `${row.competency_score.toFixed(1)} / 5` : '—'}</td>
                      <td>
                        <span className={`badge ${STATUS_BADGE[row.status]}`}>{row.status}</span>
                        {row.is_draft && (
                          <>
                            <br />
                            <small className="muted">
                              Pending with: {row.pending_with_name ?? 'no manager set'}
                              {row.approval_levels_required > 1 &&
                                ` (level ${row.current_approval_level} of ${row.approval_levels_required})`}
                            </small>
                          </>
                        )}
                      </td>
                      <td className="muted">{row.finalized_at ? formatDate(row.finalized_at) : '—'}</td>
                      <td className="right">
                        {canApprove && row.is_draft && (
                          <>
                            <IconButton icon={IconCheck} label="Approve" onClick={() => decide(row, 'approved')} />
                            <IconButton
                              icon={IconClose}
                              label="Reject"
                              variant="danger-ghost"
                              onClick={() => decide(row, 'rejected')}
                            />
                          </>
                        )}
                        {canUpdate && row.is_draft && (
                          <IconButton icon={IconEdit} label="Edit" onClick={() => openEdit(row)} />
                        )}
                        {canArchive && row.is_draft && (
                          <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={() => remove(row)} />
                        )}
                      </td>
                    </tr>,
                  ];

                  if (!isExpanded) return rows;

                  rows.push(
                    <tr key={`kpi-heading-${row.id}`}>
                      <td></td>
                      <td colSpan={8}>
                        <strong>Performance (KPIs)</strong>
                      </td>
                    </tr>
                  );

                  if (row.kpi_scores.length === 0) {
                    rows.push(
                      <tr key={`kpi-empty-${row.id}`}>
                        <td></td>
                        <td colSpan={8} className="muted">
                          No KPIs configured for this position.
                        </td>
                      </tr>
                    );
                  } else {
                    const kraGroups = new Map<string, KpiScore[]>();
                    row.kpi_scores.forEach((s) => {
                      const key = s.kra_name ?? 'Other';
                      if (!kraGroups.has(key)) kraGroups.set(key, []);
                      kraGroups.get(key)!.push(s);
                    });
                    const orderedGroups = [...kraGroups.entries()].sort((a, b) =>
                      a[0] === 'Other' ? 1 : b[0] === 'Other' ? -1 : 0
                    );

                    orderedGroups.forEach(([kraName, scores]) => {
                      rows.push(
                        <tr key={`kra-${row.id}-${kraName}`}>
                          <td></td>
                          <td colSpan={8} className="muted">
                            {kraName}
                          </td>
                        </tr>
                      );
                      scores.forEach((score) => {
                        rows.push(
                          <tr key={`kpi-${score.id}`}>
                            <td></td>
                            <td colSpan={3} style={{ paddingLeft: '1.5rem' }}>
                              {score.kpi_name}
                            </td>
                            <td className="muted">{score.weight}%</td>
                            <td colSpan={3}>
                              {canUpdate && row.is_draft ? (
                                <input
                                  type="number"
                                  step="0.5"
                                  min="1"
                                  max="5"
                                  style={{ width: 70 }}
                                  value={scoreDrafts[score.id] ?? ''}
                                  onChange={(e) => setScoreDrafts({ ...scoreDrafts, [score.id]: e.target.value })}
                                />
                              ) : (
                                (score.score ?? '—')
                              )}
                            </td>
                            <td className="right">
                              {canUpdate && row.is_draft && (
                                <IconButton icon={IconCheck} label="Save score" onClick={() => saveScore(score)} />
                              )}
                            </td>
                          </tr>
                        );
                      });
                    });

                    rows.push(
                      <tr key={`kpi-subtotal-${row.id}`}>
                        <td></td>
                        <td colSpan={8}>
                          <strong>Performance score: {row.final_score !== null ? `${row.final_score.toFixed(1)} / 5` : '—'}</strong>
                        </td>
                      </tr>
                    );
                  }

                  rows.push(
                    <tr key={`competency-heading-${row.id}`}>
                      <td></td>
                      <td colSpan={8}>
                        <strong>Competencies</strong>
                      </td>
                    </tr>
                  );

                  if (row.competency_scores.length === 0) {
                    rows.push(
                      <tr key={`competency-empty-${row.id}`}>
                        <td></td>
                        <td colSpan={8} className="muted">
                          No competencies configured.
                        </td>
                      </tr>
                    );
                  } else {
                    row.competency_scores.forEach((score) => {
                      rows.push(
                        <tr key={`competency-${score.id}`}>
                          <td></td>
                          <td colSpan={3}>{score.competency_name}</td>
                          <td className="muted">{score.weight}%</td>
                          <td colSpan={3}>
                            {canUpdate && row.is_draft ? (
                              <input
                                type="number"
                                step="0.5"
                                min="1"
                                max="5"
                                style={{ width: 70 }}
                                value={competencyScoreDrafts[score.id] ?? ''}
                                onChange={(e) => setCompetencyScoreDrafts({ ...competencyScoreDrafts, [score.id]: e.target.value })}
                              />
                            ) : (
                              (score.score ?? '—')
                            )}
                          </td>
                          <td className="right">
                            {canUpdate && row.is_draft && (
                              <IconButton icon={IconCheck} label="Save score" onClick={() => saveCompetencyScore(score)} />
                            )}
                          </td>
                        </tr>
                      );
                    });

                    rows.push(
                      <tr key={`competency-subtotal-${row.id}`}>
                        <td></td>
                        <td colSpan={8}>
                          <strong>
                            Competency score: {row.competency_score !== null ? `${row.competency_score.toFixed(1)} / 5` : '—'}
                          </strong>
                        </td>
                      </tr>
                    );
                  }

                  if (row.approvals.length === 0) {
                    rows.push(
                      <tr key={`approvals-empty-${row.id}`}>
                        <td></td>
                        <td colSpan={8} className="muted">
                          No decisions recorded yet.
                        </td>
                      </tr>
                    );
                  } else {
                    row.approvals.forEach((a, i) => {
                      rows.push(
                        <tr key={`approval-${row.id}-${i}`}>
                          <td></td>
                          <td colSpan={3} className="muted">
                            Level {a.level} — {a.approver_name ?? 'Unknown'}
                            {a.comment && <>: {a.comment}</>}
                          </td>
                          <td colSpan={3}>
                            <span className={`badge ${a.decision === 'approved' ? 'badge-active' : 'badge-cancelled'}`}>
                              {a.decision}
                            </span>
                          </td>
                          <td colSpan={2} className="muted">
                            {new Date(a.decided_at).toLocaleString('en-GB')}
                          </td>
                        </tr>
                      );
                    });
                  }

                  return rows;
                })
              ) : (
                <tr>
                  <td colSpan={8} className="muted center">
                    No reviews yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination meta={data} onPage={setPage} />
      </div>

      {adding && (
        <Modal title="Add performance review" onClose={() => setAdding(false)}>
          <form onSubmit={submitAdd} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label className="span-2">
              Name *
              <SearchableSelect<UserOption>
                endpoint="/users"
                value={form.user_id}
                onChange={(id) => setForm({ ...form, user_id: id })}
                getLabel={(u) => u.full_name}
                placeholder="Search staff…"
              />
              {errors.user_id && <small className="error">{errors.user_id[0]}</small>}
            </label>
            <label className="span-2">
              Period *
              <select value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} required>
                <option value="">Select…</option>
                {listOptions(lists, 'performance_review_period').map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
              {errors.period && <small className="error">{errors.period[0]}</small>}
            </label>
            <label className="span-2">
              Comments
              <textarea rows={3} value={form.comments} onChange={(e) => setForm({ ...form, comments: e.target.value })} />
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setAdding(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" disabled={!form.user_id || !form.period}>
                Save
              </button>
            </div>
          </form>
        </Modal>
      )}

      {editing && (
        <Modal title={`Edit review for ${editing.user_name}`} onClose={() => setEditing(null)}>
          <form onSubmit={submitEdit} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label className="span-2">
              Period *
              <select
                value={editForm.period}
                onChange={(e) => setEditForm({ ...editForm, period: e.target.value })}
                required
              >
                <option value="">Select…</option>
                {listOptions(lists, 'performance_review_period').map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
              {errors.period && <small className="error">{errors.period[0]}</small>}
            </label>
            <label className="span-2">
              Comments
              <textarea
                rows={3}
                value={editForm.comments}
                onChange={(e) => setEditForm({ ...editForm, comments: e.target.value })}
              />
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setEditing(null)}>
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
